import { useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment";

// Audit trail helpers for Pay Inputs records. Each record keeps its own
// history (newest first) in `audit`, shown by AuditTrail.js (the Audit Trail tab). Entries are
//   { id, type, title, actor: { name, role }, at, changes: [{ label, from, to }], note }
// with `from`/`to` already formatted for display. `type` is a short key the
// screen picks (e.g. "created", "updated").

let nextAuditId = 1;

export const auditEntry = ({ type, title, actor, at, changes = [], note = "" }) => ({
  id: `audit-${nextAuditId++}`,
  type,
  title,
  actor,
  at: at ?? moment().toISOString(),
  changes,
  note,
});

// Prepends an entry to the record's history.
export const withAudit = (record, entry) => ({
  ...record,
  audit: [entry, ...(record.audit ?? [])],
});

// The fields that differ between two versions of a record.
// fields: [{ key, label, format? }] in the order they should be listed.
export const diffRecords = (before, after, fields) =>
  fields
    .filter(({ key }) => before[key] !== after[key])
    .map(({ key, label, format = String }) => ({
      label,
      from: format(before[key]),
      to: format(after[key]),
    }));

// Title for an edit entry: "<Field> changed" for one field, else `fallback`.
export const editTitle = (changes, fallback) =>
  changes.length === 1 ? `${changes[0].label} changed` : fallback;

// The logged-in user, as recorded on entries made from this screen.
export const useAuditActor = () => {
  const { userDetails } = useSelector((state) => state.tokenReducer);
  return useMemo(
    () => ({
      name:
        [userDetails?.first_name, userDetails?.last_name].filter(Boolean).join(" ") ||
        userDetails?.user_name ||
        userDetails?.username ||
        "You",
      role: userDetails?.designation || "",
    }),
    [userDetails],
  );
};
